import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportSalesByProductSummary from "hooks/organization/reports/types/useGetReportSalesByProductSummary";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableSalesByProductSummary(startDate, endDate) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [productId, setProductId] = useState("");

  const [getReportSalesByProductSummary, { data, loading, error }] =
    useGetReportSalesByProductSummary(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        productId: productId || undefined,
        page,
        first,
      };
      getReportSalesByProductSummary({ variables });
    },
    [startDate, endDate, productId, getReportSalesByProductSummary]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportSalesByProductSummary?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Product",
        accessor: "product",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Total Job Size",
        accessor: "totalJobSize",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Total Material Quantity",
        accessor: "totalMaterialQuantity",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "% of Sales",
        accessor: "perOfSales",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Avg Unit Price",
        accessor: "avgUnitPrice",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportSalesByProductSummary?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportSalesByProductSummary,
    setProductId,
  } as const;
}
